/**
 * @pelipost/consumer-api-client
 *
 * Platform-agnostic HTTP client + typed domain modules for the Pelipost
 * consumer backend. Used by `apps/pelipost-mobile` (React Native) and
 * `apps/web` (React/Vite) so both platforms talk to the same API surface
 * with identical types and auth behavior.
 *
 * Usage:
 *   const api = createConsumerApiClient({
 *     mobileBaseUrl: 'https://dev-api.pelipost.com/api/v1/mobile',
 *     rootBaseUrl:   'https://dev-api.pelipost.com/api/v1',
 *     getToken:         async () => localStorage.getItem('accessToken'),
 *     getRefreshToken:  async () => localStorage.getItem('refreshToken'),
 *     setToken:         async (t) => localStorage.setItem('accessToken', t),
 *     setRefreshToken:  async (t) => localStorage.setItem('refreshToken', t),
 *     clearAuth:        async () => { localStorage.clear(); },
 *     onUnauthorized:   () => router.navigate('/login'),
 *   });
 *
 *   await api.auth.login({ email, password });
 *   const cart = await api.cart.getCart();
 */

import { ConsumerApiClient } from './client';
import type { ConsumerApiClientConfig } from './client';

import { createAuthApi } from './modules/auth';
import type { AuthApi } from './modules/auth';
import { createCartApi } from './modules/cart';
import type { CartApi } from './modules/cart';
import { createOrdersApi } from './modules/orders';
import type { OrdersApi } from './modules/orders';
import { createProductsApi } from './modules/products';
import type { ProductsApi } from './modules/products';
import { createPelipointsApi } from './modules/pelipoints';
import type { PeliPointsApi } from './modules/pelipoints';
import { createWalletApi } from './modules/wallet';
import type { WalletApi } from './modules/wallet';
import { createContactsApi } from './modules/contacts';
import type { ContactsApi } from './modules/contacts';
import { createDraftsApi } from './modules/drafts';
import type { DraftsApi } from './modules/drafts';
import { createFacilitiesApi } from './modules/facilities';
import type { FacilitiesApi } from './modules/facilities';
import { createInboxApi } from './modules/inbox';
import type { InboxApi } from './modules/inbox';
import { createMediaApi } from './modules/media';
import type { MediaApi } from './modules/media';
import { createPaymentsApi } from './modules/payments';
import type { PaymentsApi } from './modules/payments';
import { createPromotionsApi } from './modules/promotions';
import type { PromotionsApi } from './modules/promotions';
import { createAnnouncementsApi } from './modules/announcements';
import type { AnnouncementsApi } from './modules/announcements';
import { createMediaContentApi } from './modules/mediaContent';
import type { MediaContentApi } from './modules/mediaContent';
import { createAlbumApi } from './modules/album';
import type { AlbumApi } from './modules/album';
import { createSupportApi } from './modules/support';
import type { SupportApi } from './modules/support';

// ── Re-exports: transport + all module types ──────────────────

export { ConsumerApiClient };
export type {
  ConsumerApiClientConfig,
  ApiError,
  RequestOptions,
} from './client';

export * from './modules/auth';
export * from './modules/cart';
export * from './modules/orders';
export * from './modules/products';
export * from './modules/pelipoints';
export * from './modules/wallet';
export * from './modules/contacts';
export * from './modules/drafts';
export * from './modules/facilities';
export * from './modules/inbox';
export * from './modules/media';
export * from './modules/payments';
export * from './modules/promotions';
export * from './modules/announcements';
export * from './modules/mediaContent';
export * from './modules/album';
export * from './modules/support';

// ── Aggregate API shape ───────────────────────────────────────

export interface ConsumerApi {
  /** Underlying transport — exposed for escape hatches (custom endpoints, etc.). */
  client: ConsumerApiClient;

  auth: AuthApi;
  cart: CartApi;
  orders: OrdersApi;
  products: ProductsApi;
  pelipoints: PeliPointsApi;
  wallet: WalletApi;
  contacts: ContactsApi;
  drafts: DraftsApi;
  facilities: FacilitiesApi;
  inbox: InboxApi;
  media: MediaApi;
  payments: PaymentsApi;
  promotions: PromotionsApi;
  announcements: AnnouncementsApi;
  mediaContent: MediaContentApi;
  album: AlbumApi;
  support: SupportApi;
}

/**
 * Create a fully-wired consumer API surface. Instantiate once per app —
 * typically inside an AuthContext / provider — and pass the result
 * through React context so screens call `api.cart.getCart()` etc.
 */
export function createConsumerApiClient(config: ConsumerApiClientConfig): ConsumerApi {
  const client = new ConsumerApiClient(config);
  return {
    client,
    auth: createAuthApi(client),
    cart: createCartApi(client),
    orders: createOrdersApi(client),
    products: createProductsApi(client),
    pelipoints: createPelipointsApi(client),
    wallet: createWalletApi(client),
    contacts: createContactsApi(client),
    drafts: createDraftsApi(client),
    facilities: createFacilitiesApi(client),
    inbox: createInboxApi(client),
    media: createMediaApi(client),
    payments: createPaymentsApi(client),
    promotions: createPromotionsApi(client),
    announcements: createAnnouncementsApi(client),
    mediaContent: createMediaContentApi(client),
    album: createAlbumApi(client),
    support: createSupportApi(client),
  };
}
